import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Navbar as BSNavbar, Container, Nav, Button } from 'react-bootstrap';
import { authAPI, avatarsAPI } from '../services/api';
import { toast } from 'react-toastify';
import './Navbar.css';

const getInitialUser = () => {
  try {
    const s = sessionStorage.getItem('user_data');
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const formatPlanInterval = (interval) => {
  if (!interval || typeof interval !== 'string') return null;
  const i = interval.toLowerCase().trim();
  if (i === 'monthly') return 'Monthly Plan';
  if (i === 'yearly' || i === 'year') return 'Yearly Plan';
  return `${interval.charAt(0).toUpperCase()}${interval.slice(1)} Plan`;
};

const getPlanNameFromUser = (u, userId = null) => {
  if (!u) return null;

  const s = u.subscription;
  
  // 1. Explicit plan name from subscription object
  if (s?.plan?.name) return s.plan.name;
  if (s?.plan_name) return s.plan_name;
  if (s?.name) return s.name;

  // 2. Explicit plan name from user object directly
  if (typeof u.plan === 'string' && u.plan.trim()) return u.plan.trim();
  if (u.plan?.name) return u.plan.name;
  if (u.plan_name) return u.plan_name;

  // 3. Derive from subscription.plan_interval
  const subInterval = s?.plan_interval || s?.plan?.plan_interval || u.plan_interval;
  if (subInterval) return formatPlanInterval(subInterval);

  // 4. Fallback: check activated_plan in sessionStorage (set after payment)
  if (userId) {
    try {
      const key = `activated_plan_for_user_${userId}`;
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.plan_interval) return formatPlanInterval(parsed.plan_interval);
      }
    } catch (_) { /* ignore */ }
  }

  // 5. If we have a subscription object but no name found, default to a generic name
  if (s) return 'Standard Plan';
  
  return null;
};

const Navbar = ({ variant = 'default', disabled = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [user, setUser] = useState(getInitialUser);
  const [avatars, setAvatars] = useState(() => {
    try {
      const s = sessionStorage.getItem('avatars_data');
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  });
  const [plan, setPlan] = useState(() => {
    try {
      const u = getInitialUser();
      const uid = u?.id ?? u?.user_id ?? parseInt(sessionStorage.getItem('user_id') || '0', 10);
      return getPlanNameFromUser(u, uid || null);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const profileMenuRef = useRef(null);
  const userDataFetchedRef = useRef(false); // Prevent duplicate API calls
  const avatarsRefreshingRef = useRef(false); // Prevent duplicate avatar refresh calls
  const token = localStorage.getItem('access_token');

  // Check if user is logged in
  const isLoggedIn = !!token;

  // Fetch user data - only once when logged in
  useEffect(() => {
    if (isLoggedIn && !userDataFetchedRef.current) {
      fetchUserData();
    }
  }, [isLoggedIn]);

  // Sync plan when user changes or when returning from payment (activated_plan may have been set)
  useEffect(() => {
    if (user) {
      const uid = user.id ?? user.user_id ?? parseInt(sessionStorage.getItem('user_id') || '0', 10);
      const planName = getPlanNameFromUser(user, uid || null);
      setPlan(planName);
    }
  }, [user, location.pathname]);

  // Refresh avatars when navigating to chat page (e.g., after avatar creation)
  useEffect(() => {
    if (isLoggedIn && location.pathname === '/chat' && userDataFetchedRef.current && !avatarsRefreshingRef.current) {
      // Check if avatars cache was cleared (e.g., after avatar creation)
      const avatarsFetchedInSession = sessionStorage.getItem('avatars_fetched');
      const storedAvatarsData = sessionStorage.getItem('avatars_data');

      // If avatars cache is cleared (indicating avatar creation happened), refresh avatars
      // This ensures that after avatar creation, the Navbar shows the new avatar
      if (!avatarsFetchedInSession || !storedAvatarsData) {
        const userId = user?.id || user?.user_id || parseInt(sessionStorage.getItem('user_id') || '0', 10);
        if (userId && userId > 0) {
          console.log('Navbar: Avatars cache missing or cleared after avatar creation, refreshing avatars list...');
          avatarsRefreshingRef.current = true; // Prevent duplicate calls

          // Use a small delay to ensure avatar creation API call has completed
          const timeoutId = setTimeout(() => {
            refreshAvatars(userId).finally(() => {
              avatarsRefreshingRef.current = false; // Reset after refresh completes
            });
          }, 1500); // 1.5 second delay to allow avatar creation to complete and be available in API

          return () => {
            clearTimeout(timeoutId);
            avatarsRefreshingRef.current = false;
          };
        }
      }
    }
  }, [location.pathname, isLoggedIn]);

  // Refresh user data when navigating if cache was cleared (e.g., after payment or avatar creation)
  useEffect(() => {
    if (isLoggedIn && !sessionStorage.getItem('user_fetched')) {
      console.log('Navbar: User cache cleared, re-fetching user data...');
      userDataFetchedRef.current = false;
      fetchUserData();
    }
  }, [location.pathname, isLoggedIn]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const fetchUserData = async () => {
    // Check if already fetched in this session (across page navigations)
    const userFetchedInSession = sessionStorage.getItem('user_fetched');
    const avatarsFetchedInSession = sessionStorage.getItem('avatars_fetched');

    // Try to get user data from sessionStorage first (might be set by other components)
    const storedUserData = sessionStorage.getItem('user_data');
    if (storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        setUser(parsedUser);
        console.log('Navbar: Loaded user data from sessionStorage:', parsedUser);

        // Also set plan if available (pass userId for activated_plan fallback)
        const uid = parsedUser?.id ?? parsedUser?.user_id ?? parseInt(sessionStorage.getItem('user_id') || '0', 10);
        const planName = getPlanNameFromUser(parsedUser, uid || null);
        setPlan(planName);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }

    // Try to get avatars from sessionStorage if available
    const storedAvatarsData = sessionStorage.getItem('avatars_data');
    if (storedAvatarsData) {
      try {
        const parsedAvatars = JSON.parse(storedAvatarsData);
        setAvatars(parsedAvatars);
        console.log('Navbar: Loaded avatars from sessionStorage:', parsedAvatars.length);
      } catch (error) {
        console.error('Error parsing stored avatars data:', error);
      }
    }

    // If both are already fetched in session (e.g., by LandingPage), skip all API calls
    if (userFetchedInSession && avatarsFetchedInSession) {
      console.log('Navbar: User and avatars already fetched in this session, skipping all API calls');
      return;
    }

    // If already fetched by this component, skip
    if (userDataFetchedRef.current) {
      console.log('Navbar: Already fetched by this component, skipping API calls');
      return;
    }

    try {
      userDataFetchedRef.current = true; // Mark as fetching immediately
      setLoading(true);

      // Only call getCurrentUser if not already fetched anywhere
      let userId = null;
      if (!userFetchedInSession) {
        // Set flag immediately to prevent duplicate calls (synchronous)
        sessionStorage.setItem('user_fetched', 'true');
        console.log('Navbar: Fetching user data (first time only)...');
        const userResponse = await authAPI.getCurrentUser();
        if (userResponse.status && userResponse.data) {
          const userData = userResponse.data;
          console.log('Navbar: User data received from API:', userData);
          console.log('Navbar: User name fields - first_name:', userData.first_name, 'last_name:', userData.last_name, 'email:', userData.email);
          setUser(userData);
          userId = userData.id || userData.user_id;

          // Store user data in sessionStorage for other components to use
          try {
            sessionStorage.setItem('user_data', JSON.stringify(userData));
          } catch (error) {
            console.warn('Could not store user data in sessionStorage:', error);
          }

          // Store userId in sessionStorage so other components can use it
          if (userId) {
            sessionStorage.setItem('user_id', userId.toString());
          }

          // Set plan if available (pass userId for activated_plan fallback)
          const planName = getPlanNameFromUser(userData, userId || null);
          setPlan(planName);
        }
      } else {
        console.log('Navbar: User already fetched, skipping getCurrentUser call');
        // Get userId from sessionStorage if available (set by LandingPage or previous fetch)
        const storedUserId = sessionStorage.getItem('user_id');
        if (storedUserId) {
          userId = parseInt(storedUserId, 10);
        }

        // If user data is not set yet but we have cached data, load it
        if (!user && storedUserData) {
          try {
            const parsedUser = JSON.parse(storedUserData);
            setUser(parsedUser);
            const uid = parsedUser?.id ?? parsedUser?.user_id ?? userId;
            const planName = getPlanNameFromUser(parsedUser, uid || null);
            setPlan(planName);
          } catch (error) {
            console.error('Error parsing stored user data:', error);
          }
        }
      }

      // Fetch avatars only once if userId is available and not already fetched
      if (userId && !avatarsFetchedInSession) {
        // Set flag immediately to prevent duplicate calls (synchronous)
        sessionStorage.setItem('avatars_fetched', 'true');
        try {
          console.log('Navbar: Fetching avatars (first time only)...');
          const avatarsResponse = await avatarsAPI.getAvatars(userId);
          if (avatarsResponse.status && avatarsResponse.data) {
            const avatars = avatarsResponse.data.avatars || [];
            setAvatars(avatars);
            const hasAvatarsValue = avatars.length > 0;
            sessionStorage.setItem('has_avatars', hasAvatarsValue.toString()); // Cache hasAvatars

            // Store avatars data in sessionStorage
            try {
              sessionStorage.setItem('avatars_data', JSON.stringify(avatars));
            } catch (error) {
              console.warn('Could not store avatars data in sessionStorage:', error);
            }
          }
        } catch (error) {
          console.error('Error fetching avatars:', error);
          sessionStorage.removeItem('avatars_fetched'); // Remove flag on error
        }
      } else if (avatarsFetchedInSession) {
        console.log('Navbar: Avatars already fetched in session, skipping getAvatars call');
      } else if (!userId) {
        console.warn('Navbar: No userId available, cannot fetch avatars');
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
      userDataFetchedRef.current = false; // Reset on error so it can retry
      sessionStorage.removeItem('user_fetched');
      sessionStorage.removeItem('avatars_fetched');
      sessionStorage.removeItem('has_avatars');
      sessionStorage.removeItem('user_id');
      sessionStorage.removeItem('user_data');
      sessionStorage.removeItem('avatars_data');
    } finally {
      setLoading(false);
    }
  };

  // Refresh avatars list - can be called after avatar creation
  const refreshAvatars = async (userId) => {
    if (!userId) {
      const storedUserId = sessionStorage.getItem('user_id');
      if (storedUserId) {
        userId = parseInt(storedUserId, 10);
      } else {
        console.warn('Navbar: Cannot refresh avatars - no userId available');
        return;
      }
    }

    try {
      console.log('Navbar: Refreshing avatars list after avatar creation...');
      setLoading(true);

      // Don't clear the fetched flag here - we want to fetch fresh data
      const avatarsResponse = await avatarsAPI.getAvatars(userId);
      if (avatarsResponse.status && avatarsResponse.data) {
        const fetchedAvatars = avatarsResponse.data.avatars || [];
        setAvatars(fetchedAvatars);
        const hasAvatarsValue = fetchedAvatars.length > 0;

        // Update sessionStorage with fresh data
        sessionStorage.setItem('avatars_fetched', 'true');
        sessionStorage.setItem('has_avatars', hasAvatarsValue.toString());
        try {
          sessionStorage.setItem('avatars_data', JSON.stringify(fetchedAvatars));
        } catch (error) {
          console.warn('Could not store avatars data in sessionStorage:', error);
        }

        console.log('Navbar: Avatars refreshed successfully. Count:', fetchedAvatars.length);
        if (fetchedAvatars.length > 0) {
          console.log('Navbar: First avatar:', fetchedAvatars[0]);
        }
      }
    } catch (error) {
      console.error('Error refreshing avatars:', error);
      // Don't show error toast for refresh failures - just log it
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Clear chat-related localStorage (user-specific; prevents 403 when new user signs in)
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_session_project_id');
    localStorage.removeItem('chat_messages');
    // Clear all sessionStorage data on logout
    sessionStorage.removeItem('user_fetched');
    sessionStorage.removeItem('avatars_fetched');
    sessionStorage.removeItem('has_avatars');
    sessionStorage.removeItem('user_id');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('avatars_data');
    sessionStorage.removeItem('plans_fetched');
    localStorage.removeItem('signup_invite_params');
    // Reset component state
    setUser(null);
    setAvatars([]);
    setPlan(null);
    userDataFetchedRef.current = false;
    toast.success('Signed out successfully');
    navigate('/');
    setShowProfileMenu(false);
  };

  const getUserInitials = () => {
    if (user) {
      const firstName = user.first_name || user.firstName || user.given_name || '';
      const lastName = user.last_name || user.lastName || user.family_name || '';
      if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      }
      if (firstName) return firstName.charAt(0).toUpperCase();
      if (lastName) return lastName.charAt(0).toUpperCase();
      if (user.email) return user.email.charAt(0).toUpperCase();
      if (user.full_name) {
        const parts = user.full_name.trim().split(/\s+/);
        if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
        if (parts[0]) return parts[0].charAt(0).toUpperCase();
      }
      if (user.username) return user.username.charAt(0).toUpperCase();
      if (user.name) {
        const parts = user.name.trim().split(/\s+/);
        if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
        if (parts[0]) return parts[0].charAt(0).toUpperCase();
      }
    }
    return 'U';
  };

  const getDisplayName = () => {
    if (!user) return null;
    const first = user.first_name || user.firstName || user.given_name || '';
    const last = user.last_name || user.lastName || user.family_name || '';
    const fullName = `${first} ${last}`.trim();
    return fullName || user.full_name || user.username || user.name || user.email || null;
  };

  const handleCreateAvatarClick = async (e) => {
    if (e) e.preventDefault();

    if (!isLoggedIn) {
      navigate('/signin');
      return;
    }

    // Always navigate to the creation page. 
    // If the user already has avatars, they should still be able to create new ones 
    // (especially paid "Loved One" avatars) through this link.
    // Enforcing limits happens on the CreateAvatarPage itself.
    navigate('/create-avatar');
  };

  return (
    <BSNavbar
      className={`custom-navbar ${variant === 'transparent' ? 'navbar-transparent' : ''}`}
      expand="lg"
      fixed="top"
    >
      <Container fluid className="navbar-container">
        <Link
          to="/"
          className="logo"
          style={disabled ? { pointerEvents: 'none', opacity: 0.8 } : {}}
          onClick={disabled ? (e) => e.preventDefault() : undefined}
        >
          <img src={process.env.PUBLIC_URL + '/Sayagain.png'} alt="SayAgain" className="logo-img" />
        </Link>
        {location.pathname !== '/create-avatar' && (
        <Nav className="nav-links">
          <a
            href="/create-avatar"
            className={`nav-link-custom ${location.pathname === '/create-avatar' || location.pathname === '/chat' ? 'active' : ''}`}
            onClick={handleCreateAvatarClick}
            style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
          >
            Create Avatar
          </a>
          <Link
            to="/chat"
            className={`nav-link-custom ${location.pathname === '/chat' ? 'active' : ''}`}
            style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
            onClick={disabled ? (e) => e.preventDefault() : undefined}
          >
            Chat
          </Link>

          {isLoggedIn ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div className="profile-menu-wrapper" ref={profileMenuRef}>
              <button
                className="profile-menu-trigger"
                onClick={() => !disabled && setShowProfileMenu(!showProfileMenu)}
                style={disabled ? { cursor: 'default', opacity: 0.8 } : {}}
                disabled={disabled}
              >
                <div className="profile-avatar-circle">
                  {getUserInitials()}
                </div>
              </button>

              {showProfileMenu && (
                <div className="profile-menu-dropdown">
                  <div
                    className="profile-menu-header"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowProfileMenu(false);
                      navigate('/chat');
                    }}
                  >
                    <div className="profile-menu-avatar">
                      {getUserInitials()}
                    </div>
                    <div className="profile-menu-info">
                      <div className="profile-menu-name">
                        {getDisplayName() || user?.email || 'User'}
                      </div>
                      <div className="profile-menu-email">{user?.email || ''}</div>
                    </div>
                  </div>

                  <div className="profile-menu-section">
                    <div className="profile-menu-section-title">Avatars</div>
                    <div className="profile-menu-avatars">
                      {loading ? (
                        <div className="profile-menu-loading">Loading...</div>
                      ) : avatars.length > 0 ? (
                        avatars.map((avatar) => (
                          <Link
                            key={avatar.id}
                            to={`/avatar/${avatar.id}`}
                            className="profile-menu-avatar-item"
                            onClick={() => setShowProfileMenu(false)}
                          >
                            <div className="profile-menu-avatar-initials">
                              {`${avatar.first_name?.charAt(0) || ''}${avatar.last_name?.charAt(0) || ''}`.toUpperCase()}
                            </div>
                            <div className="profile-menu-avatar-name">
                              {`${avatar.first_name || ''} ${avatar.last_name || ''}`.trim()}
                            </div>
                            <div className="profile-menu-avatar-status">
                              {avatar.status === 'active' ? '● Active' : ''}
                            </div>
                          </Link>
                        ))
                      ) : (
                        <div className="profile-menu-no-avatars">No avatars yet</div>
                      )}
                    </div>
                  </div>


                  <div className="profile-menu-actions">
                    <button
                      onClick={handleLogout}
                      className="profile-menu-action profile-menu-logout"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
            <>
              {location.pathname === '/signin' ? (
                // On signin page, show "Create Account" button
                <Link to="/signup">
                  <Button className="nav-cta">Create Account</Button>
                </Link>
              ) : location.pathname === '/signup' ? (
                // On signup page, show "Login" button
                <Link to="/signin">
                  <Button className="nav-cta">Login</Button>
                </Link>
              ) : (
                // On landing page and other pages, show "Login" button (default after signout)
                <Link to="/signin">
                  <Button className="nav-cta">Login</Button>
                </Link>
              )}
            </>
          )}
        </Nav>
        )}
      </Container>
    </BSNavbar>
  );
};

export default Navbar;

